import {
  format,
  parseISO,
  formatDistanceToNow,
  differenceInYears,
  isValid,
} from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Converts a local date to UTC for sending to backend
 * @param localDate - Date in local timezone
 * @returns Date in ISO UTC format
 */
export const toUTC = (localDate: Date): string => {
  return localDate.toISOString();
};

/**
 * Converts a UTC date from backend to local date
 * @param utcString - Date in ISO UTC format
 * @returns Date object in local timezone
 */
export const fromUTC = (utcString: string): Date => {
  return parseISO(utcString);
};

/**
 * Formats a UTC date to display without timezone conversion
 * @param utcDate - Date in ISO UTC format
 * @param formatString - Date format (default: 'dd MMM yyyy')
 * @returns Formatted date without timezone conversion
 */
export const formatDateForUser = (
  utcDate: string,
  formatString = 'dd MMM yyyy',
): string => {
  try {
    const date = parseISO(utcDate);
    return format(date, formatString, { locale: es });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Fecha no disponible';
  }
};

/**
 * Formats a date for date input (YYYY-MM-DD)
 * Maintains the same date when displayed in user's timezone
 * @param localDate - Local date string or Date object
 * @returns Date in YYYY-MM-DD format
 */
export const toDateInputFormat = (localDate: Date | string): string => {
  const date = typeof localDate === 'string' ? fromUTC(localDate) : localDate;
  return format(date, 'yyyy-MM-dd');
};

/**
 * Converts a date input value (YYYY-MM-DD) to UTC string
 * Maintains the same date in the user's timezone
 * @param dateInputValue - Input value in YYYY-MM-DD format
 * @returns Date in ISO UTC format
 */
export const fromDateInputToUTC = (dateInputValue: string): string => {
  // Create a local date from the input value (user's timezone)
  const [year, month, day] = dateInputValue.split('-').map(Number);
  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);

  // Convert to UTC string
  return localDate.toISOString();
};

/**
 * Formats time for display in user's local timezone
 * @param date - Date in ISO UTC format or Date object
 * @returns Formatted time in HH:mm format
 */
export const formatTime = (date: Date | string): string => {
  try {
    const dateObj = typeof date === 'string' ? fromUTC(date) : date;
    return format(dateObj, 'HH:mm');
  } catch (error) {
    console.error('Error formatting time:', error);
    return '--:--';
  }
};

/**
 * Formats a UTC time to display to user in their local timezone
 * @param utcDate - Date in ISO UTC format
 * @returns Formatted time in HH:mm format
 */
export const formatTimeForUser = (utcDate: string): string => {
  try {
    const localDate = fromUTC(utcDate);
    return format(localDate, 'HH:mm');
  } catch (error) {
    console.error('Error formatting time:', error);
    return '--:--';
  }
};

/**
 * Calculates remaining time from now to a UTC date
 * @param utcDate - Date in ISO UTC format
 * @returns Formatted remaining time
 */
export const getTimeRemainingFromUTC = (utcDate: string): string =>
  getTimeRemainingFromDate(fromUTC(utcDate));

/**
 * Calculates remaining time from now to an already parsed local date
 * @param localDate - Date in the user's timezone
 * @returns Formatted remaining time
 */
export const getTimeRemainingFromDate = (localDate: Date): string => {
  try {
    if (!isValid(localDate)) return 'No disponible';

    if (localDate > new Date()) {
      return formatDistanceToNow(localDate, { addSuffix: true, locale: es });
    }
    return 'Finalizado';
  } catch (error) {
    console.error('Error calculating time remaining:', error);
    return 'No disponible';
  }
};

/**
 * Parses a backend timestamp that the API serializes as a zone-less ISO string
 * whose value is already UTC (Kotlin `LocalDateTime` written via
 * `nowLocalDateTimeInUTC()`, with Jackson's WRITE_DATES_AS_TIMESTAMPS off).
 * Without the explicit `Z`, `parseISO` would read it as local time and shift
 * the result by the browser's offset.
 *
 * Only for fields the backend does NOT timezone-convert before serializing:
 * `registration.createdAt`, `earnedAt`, the debt-payment timestamps. Tournament
 * and match dates go through `convertUtcToMatchTimeZone` in their facades and
 * already arrive as venue-local wall clock — use {@link fromUTC} for those.
 */
export function parseUtcIso(iso: string | undefined | null): Date | null {
  const trimmed = iso?.trim();
  if (!trimmed) return null;
  const timePart = trimmed.slice(trimmed.indexOf('T') + 1);
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(timePart);
  const parsed = parseISO(hasOffset ? trimmed : `${trimmed}Z`);
  return isValid(parsed) ? parsed : null;
}

/**
 * A {@link parseUtcIso} timestamp rendered in the browser's timezone, or null
 * when it is missing or unparseable so callers can omit the line entirely.
 */
export function formatUtcDateTimeForUser(
  iso: string | undefined | null,
): string | null {
  const date = parseUtcIso(iso);
  if (!date) return null;
  return format(date, "dd/MM/yyyy 'a las' HH:mm", { locale: es });
}

/**
 * Parses a player birthdate from ISO (with or without time). Uses local calendar date for YYYY-MM-DD.
 */
export function parseBirthdateIso(
  iso: string | undefined | null,
): Date | null {
  if (!iso || typeof iso !== 'string' || !iso.trim()) return null;
  const s = iso.trim();
  try {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    const d = dateOnly
      ? new Date(
          Number(dateOnly[1]),
          Number(dateOnly[2]) - 1,
          Number(dateOnly[3]),
        )
      : parseISO(s);
    if (!isValid(d) || Number.isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

/** Age in full years from today, or null if birthdate is missing/invalid. */
export function getAgeFromBirthdateIso(
  iso: string | undefined | null,
): number | null {
  const birth = parseBirthdateIso(iso);
  if (!birth) return null;
  return differenceInYears(new Date(), birth);
}

/** Formatted birthdate for UI (es-AR style), or null if invalid. */
export function formatBirthdateForDisplay(
  iso: string | undefined | null,
): string | null {
  const d = parseBirthdateIso(iso);
  if (!d) return null;
  return format(d, 'dd/MM/yyyy', { locale: es });
}
